<script setup lang="ts">
import { ref, onMounted, computed } from 'vue'
import { Roles } from '@/constants.ts'
import diagnosticsHttpService, {
  type AppSettingsResponse,
  type AppSettingsLanguage,
} from '@/services/http/diagnosticsHttpService.ts'
import agentsHttpService from '@/services/http/agentsHttpService.ts'
import mailingsHttpService from '@/services/http/mailingsHttpService.ts'
import productsHttpService from '@/services/http/productsHttpService.ts'
import type {
  SmsTemplateModel,
  EmailTemplateModel,
  ProductDetailModel,
} from '@/services/http/httpResponses.ts'

definePage({
  meta: {
    allowedRoles: [Roles.Admin, Roles.CareerGuidanceSpecialist, Roles.Agent],
  },
})

const PRODUCT_CODE = 'CGT'

const userFrontendKeys = {
  clarityKey: !!import.meta.env.VITE_CLARITY_KEY,
  tipTopPublicKey: !!import.meta.env.VITE_TIPTOP_PUBLIC_KEY,
  yandexAnalyticsId: !!import.meta.env.VITE_YANDEX_ANALYTICS_ID,
  tawkTo: true,
}

const appSettings = ref<AppSettingsResponse | null>(null)
const appUrl = ref<string>('')
const rewardPercent = ref<number | null>(null)
const smsTemplates = ref<SmsTemplateModel[]>([])
const emailTemplates = ref<EmailTemplateModel[]>([])
const productDetails = ref<ProductDetailModel[]>([])

const enabledLanguages = computed<AppSettingsLanguage[]>(
  () => appSettings.value?.languages.filter((l) => l.enabled) ?? [],
)

const activeTariff = computed(() => {
  const now = new Date()
  return (
    productDetails.value.find((d) => {
      const begin = new Date(d.dateBegin)
      const end = d.dateEnd ? new Date(d.dateEnd) : null
      return begin <= now && (end === null || end > now)
    }) ?? null
  )
})

type TemplateRow = { code: string; smsLangs: string[]; emailLangs: string[] }

const templateRows = computed<TemplateRow[]>(() => {
  const codes = Array.from(
    new Set([...smsTemplates.value.map((t) => t.code), ...emailTemplates.value.map((t) => t.code)]),
  ).sort()
  return codes.map((code) => ({
    code,
    smsLangs:
      smsTemplates.value.find((t) => t.code === code)?.translations.map((tr) => tr.languageCode) ??
      [],
    emailLangs:
      emailTemplates.value
        .find((t) => t.code === code)
        ?.translations.map((tr) => tr.languageCode) ?? [],
  }))
})

onMounted(async () => {
  await Promise.allSettled([
    diagnosticsHttpService.getAppSettings().then((r) => {
      appSettings.value = r
      appUrl.value = r.notifications.appUrl
    }),
    agentsHttpService.getAgentSettings().then((r) => {
      rewardPercent.value = r.rewardPercent
    }),
    mailingsHttpService.getSmsTemplates().then((r) => {
      smsTemplates.value = r.templates
    }),
    mailingsHttpService.getEmailTemplates().then((r) => {
      emailTemplates.value = r.templates
    }),
    productsHttpService.getProductDetails(PRODUCT_CODE).then((r) => {
      productDetails.value = r.details
    }),
  ])
})
</script>

<template>
  <div class="p-6 max-w-6xl mx-auto">
    <div v-if="appSettings">
      <h1 class="text-xl font-bold text-gray-800 mb-5">Настройки системы</h1>
      <div class="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <!-- Бизнес -->
        <div class="border rounded-xl overflow-hidden shadow-sm border-l-4 border-l-emerald-400">
          <div
            class="px-4 py-2.5 bg-gray-50 text-xs font-semibold text-emerald-600 uppercase tracking-widest border-b"
          >
            Бизнес
          </div>
          <table class="w-full text-sm">
            <tbody>
              <tr class="border-b">
                <td
                  class="px-4 py-2.5 w-8 text-center cursor-default"
                  title="БД: AgentSettings.RewardPercent"
                >
                  <span :class="rewardPercent != null ? 'text-green-500' : 'text-red-500'"
                    ><i
                      :class="
                        rewardPercent != null ? 'pi pi-check-circle' : 'pi pi-exclamation-triangle'
                      "
                  /></span>
                </td>
                <td class="px-4 py-2.5 text-gray-700">Агентское вознаграждение</td>
                <td class="px-4 py-2.5 text-right text-xs text-gray-400 font-mono">
                  {{ rewardPercent != null ? rewardPercent + '%' : 'не задано' }}
                </td>
              </tr>
              <tr>
                <td
                  class="px-4 py-2.5 w-8 text-center cursor-default"
                  title="БД: ProductDetails (код CGT, активный период)"
                >
                  <span :class="activeTariff != null ? 'text-green-500' : 'text-red-500'"
                    ><i
                      :class="
                        activeTariff != null ? 'pi pi-check-circle' : 'pi pi-exclamation-triangle'
                      "
                  /></span>
                </td>
                <td class="px-4 py-2.5 text-gray-700">Тариф ({{ PRODUCT_CODE }})</td>
                <td class="px-4 py-2.5 text-right text-xs text-gray-400 font-mono">
                  {{ activeTariff != null ? activeTariff.price + ' ₸' : 'не задан' }}
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <!-- Авторизация -->
        <div class="border rounded-xl overflow-hidden shadow-sm border-l-4 border-l-slate-400">
          <div
            class="px-4 py-2.5 bg-gray-50 text-xs font-semibold text-slate-600 uppercase tracking-widest border-b"
          >
            Авторизация
          </div>
          <table class="w-full text-sm">
            <tbody>
              <tr class="border-b">
                <td
                  class="px-4 py-2.5 w-8 text-center text-green-500 cursor-default"
                  title="AuthSettings:AccessTokenLifetime"
                >
                  <i class="pi pi-key" />
                </td>
                <td class="px-4 py-2.5 text-gray-700">Access token</td>
                <td class="px-4 py-2.5 text-right text-xs text-gray-400 font-mono">
                  {{ appSettings.auth.accessTokenLifetimeMinutes }} мин
                </td>
              </tr>
              <tr>
                <td
                  class="px-4 py-2.5 w-8 text-center text-green-500 cursor-default"
                  title="AuthSettings:RefreshTokenLifetime"
                >
                  <i class="pi pi-refresh" />
                </td>
                <td class="px-4 py-2.5 text-gray-700">Refresh token</td>
                <td class="px-4 py-2.5 text-right text-xs text-gray-400 font-mono">
                  {{ appSettings.auth.refreshTokenLifetimeDays }} дн
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <!-- Уведомления -->
        <div class="border rounded-xl overflow-hidden shadow-sm border-l-4 border-l-violet-400">
          <div
            class="px-4 py-2.5 bg-gray-50 text-xs font-semibold text-violet-600 uppercase tracking-widest border-b"
          >
            Уведомления
          </div>
          <table class="w-full text-sm">
            <tbody>
              <tr class="border-b bg-violet-50/40">
                <td class="px-4 py-2 w-8 text-center text-violet-400">
                  <i class="pi pi-external-link text-xs" />
                </td>
                <td class="px-4 py-2" colspan="2">
                  <a
                    href="https://account.postmarkapp.com/servers"
                    target="_blank"
                    class="text-xs text-violet-600 underline font-medium"
                    >Postmark</a
                  >
                </td>
              </tr>
              <tr class="border-b bg-violet-50/40">
                <td class="px-4 py-2 w-8 text-center text-violet-400">
                  <i class="pi pi-external-link text-xs" />
                </td>
                <td class="px-4 py-2" colspan="2">
                  <a
                    href="https://mobizon.kz/panel"
                    target="_blank"
                    class="text-xs text-violet-600 underline font-medium"
                    >Mobizon</a
                  >
                </td>
              </tr>
              <tr class="border-b">
                <td
                  class="px-4 py-2.5 w-8 text-center cursor-default"
                  title="NotificationConfiguration:AppUrl"
                >
                  <span
                    :class="appSettings.notifications.appUrl ? 'text-green-500' : 'text-red-500'"
                    ><i
                      :class="
                        appSettings.notifications.appUrl
                          ? 'pi pi-check-circle'
                          : 'pi pi-exclamation-triangle'
                      "
                  /></span>
                </td>
                <td class="px-4 py-2.5 text-gray-700">Хост пользователей</td>
                <td class="px-4 py-2.5 text-right text-xs font-mono">
                  <a
                    v-if="appSettings.notifications.appUrl"
                    :href="appSettings.notifications.appUrl"
                    target="_blank"
                    class="text-blue-500 hover:underline"
                    >{{ appSettings.notifications.appUrl }}</a
                  >
                  <span v-else class="text-gray-400">—</span>
                </td>
              </tr>
              <tr class="border-b">
                <td
                  class="px-4 py-2.5 w-8 text-center cursor-default"
                  title="NotificationConfiguration:AdminUrl"
                >
                  <span
                    :class="appSettings.notifications.adminUrl ? 'text-green-500' : 'text-red-500'"
                    ><i
                      :class="
                        appSettings.notifications.adminUrl
                          ? 'pi pi-check-circle'
                          : 'pi pi-exclamation-triangle'
                      "
                  /></span>
                </td>
                <td class="px-4 py-2.5 text-gray-700">Хост администраторов</td>
                <td class="px-4 py-2.5 text-right text-xs font-mono">
                  <a
                    v-if="appSettings.notifications.adminUrl"
                    :href="appSettings.notifications.adminUrl"
                    target="_blank"
                    class="text-blue-500 hover:underline"
                    >{{ appSettings.notifications.adminUrl }}</a
                  >
                  <span v-else class="text-gray-400">—</span>
                </td>
              </tr>
              <tr class="border-b">
                <td
                  class="px-4 py-2.5 w-8 text-center cursor-default"
                  title="EmailOptions:ApiKey | EmailOptions:IsRealApi"
                >
                  <span
                    :class="
                      appSettings.notifications.emailApiKeyConfigured
                        ? 'text-green-500'
                        : 'text-red-500'
                    "
                    ><i
                      :class="
                        appSettings.notifications.emailApiKeyConfigured
                          ? 'pi pi-check-circle'
                          : 'pi pi-exclamation-triangle'
                      "
                  /></span>
                </td>
                <td class="px-4 py-2.5 text-gray-700">Postmark API Key</td>
                <td
                  class="px-4 py-2.5 text-right text-xs font-mono"
                  :class="
                    appSettings.notifications.emailIsRealApi ? 'text-green-600' : 'text-yellow-600'
                  "
                >
                  {{ appSettings.notifications.emailIsRealApi ? 'Боевой' : 'Тестовый' }}
                </td>
              </tr>
              <tr class="border-b">
                <td
                  class="px-4 py-2.5 w-8 text-center text-green-500 cursor-default"
                  title="EmailOptions:Sender"
                >
                  <i class="pi pi-envelope" />
                </td>
                <td class="px-4 py-2.5 text-gray-700">Email отправитель</td>
                <td class="px-4 py-2.5 text-right text-xs text-gray-400 font-mono">
                  {{ appSettings.notifications.emailSender }}
                </td>
              </tr>
              <tr class="border-b">
                <td
                  class="px-4 py-2.5 w-8 text-center cursor-default"
                  title="SmsOptions:ApiKey | SmsOptions:IsRealApi"
                >
                  <span
                    :class="
                      appSettings.notifications.smsApiKeyConfigured
                        ? 'text-green-500'
                        : 'text-red-500'
                    "
                    ><i
                      :class="
                        appSettings.notifications.smsApiKeyConfigured
                          ? 'pi pi-check-circle'
                          : 'pi pi-exclamation-triangle'
                      "
                  /></span>
                </td>
                <td class="px-4 py-2.5 text-gray-700">Mobizon API Key</td>
                <td
                  class="px-4 py-2.5 text-right text-xs font-mono"
                  :class="
                    appSettings.notifications.smsIsRealApi ? 'text-green-600' : 'text-yellow-600'
                  "
                >
                  {{ appSettings.notifications.smsIsRealApi ? 'Боевой' : 'Тестовый' }}
                </td>
              </tr>
              <tr>
                <td
                  class="px-4 py-2.5 w-8 text-center text-green-500 cursor-default"
                  title="NotificationOptions:VerificationNotificationInterval"
                >
                  <i class="pi pi-clock" />
                </td>
                <td class="px-4 py-2.5 text-gray-700">Интервал повторной верификации</td>
                <td class="px-4 py-2.5 text-right text-xs text-gray-400 font-mono">
                  {{ appSettings.notifications.verificationIntervalSeconds }}с
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <!-- Платежи -->
        <div class="border rounded-xl overflow-hidden shadow-sm border-l-4 border-l-orange-400">
          <div
            class="px-4 py-2.5 bg-gray-50 text-xs font-semibold text-orange-600 uppercase tracking-widest border-b"
          >
            Платежи (TipTop)
          </div>
          <table class="w-full text-sm">
            <tbody>
              <tr class="border-b bg-orange-50/40">
                <td class="px-4 py-2 w-8 text-center text-orange-400">
                  <i class="pi pi-external-link text-xs" />
                </td>
                <td class="px-4 py-2" colspan="2">
                  <a
                    href="https://merchant.tiptoppay.kz/"
                    target="_blank"
                    class="text-xs text-orange-600 underline font-medium"
                    >merchant.tiptoppay.kz</a
                  >
                </td>
              </tr>
              <tr class="border-b bg-orange-50/40">
                <td class="px-4 py-2 w-8 text-center text-orange-400">
                  <i class="pi pi-external-link text-xs" />
                </td>
                <td class="px-4 py-2" colspan="2">
                  <a
                    href="https://org.oofd.kz/"
                    target="_blank"
                    class="text-xs text-orange-600 underline font-medium"
                    >org.oofd.kz (ООФД)</a
                  >
                </td>
              </tr>
              <tr class="border-b">
                <td class="px-4 py-2.5 w-8 text-center cursor-default" title="TipTop:PublicId">
                  <span
                    :class="
                      appSettings.payments.publicIdConfigured ? 'text-green-500' : 'text-red-500'
                    "
                    ><i
                      :class="
                        appSettings.payments.publicIdConfigured
                          ? 'pi pi-check-circle'
                          : 'pi pi-exclamation-triangle'
                      "
                  /></span>
                </td>
                <td class="px-4 py-2.5 text-gray-700">Public ID</td>
                <td class="px-4 py-2.5 text-right text-xs text-gray-400 font-mono">
                  {{ appSettings.payments.publicId || '—' }}
                </td>
              </tr>
              <tr class="border-b">
                <td class="px-4 py-2.5 w-8 text-center cursor-default" title="TipTop:ApiSecret">
                  <span
                    :class="
                      appSettings.payments.apiSecretConfigured ? 'text-green-500' : 'text-red-500'
                    "
                    ><i
                      :class="
                        appSettings.payments.apiSecretConfigured
                          ? 'pi pi-check-circle'
                          : 'pi pi-exclamation-triangle'
                      "
                  /></span>
                </td>
                <td class="px-4 py-2.5 text-gray-700">API Secret</td>
                <td class="px-4 py-2.5 text-right text-xs text-gray-400 font-mono">
                  {{ appSettings.payments.apiSecretConfigured ? '••••' : '—' }}
                </td>
              </tr>
              <tr class="border-b">
                <td class="px-4 py-2.5 w-8 text-center cursor-default" title="TipTop:Inn">
                  <span
                    :class="appSettings.payments.innConfigured ? 'text-green-500' : 'text-red-500'"
                    ><i
                      :class="
                        appSettings.payments.innConfigured
                          ? 'pi pi-check-circle'
                          : 'pi pi-exclamation-triangle'
                      "
                  /></span>
                </td>
                <td class="px-4 py-2.5 text-gray-700">ИИН/БИН</td>
                <td class="px-4 py-2.5 text-right text-xs text-gray-400 font-mono">
                  {{ appSettings.payments.inn || '—' }}
                </td>
              </tr>
              <tr class="border-b">
                <td
                  class="px-4 py-2.5 w-8 text-center text-green-500 cursor-default"
                  title="TipTop:ApiBaseUrl"
                >
                  <i class="pi pi-link" />
                </td>
                <td class="px-4 py-2.5 text-gray-700">API URL</td>
                <td class="px-4 py-2.5 text-right text-xs text-gray-400 font-mono">
                  {{ appSettings.payments.apiBaseUrl }}
                </td>
              </tr>
              <tr>
                <td
                  class="px-4 py-2.5 w-8 text-center text-green-500 cursor-default"
                  title="TipTop:CalculationPlace"
                >
                  <i class="pi pi-map-marker" />
                </td>
                <td class="px-4 py-2.5 text-gray-700">Место расчёта</td>
                <td class="px-4 py-2.5 text-right text-xs text-gray-400 font-mono">
                  {{ appSettings.payments.calculationPlace }}
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <!-- ИИ -->
        <div class="border rounded-xl overflow-hidden shadow-sm border-l-4 border-l-purple-400">
          <div
            class="px-4 py-2.5 bg-gray-50 text-xs font-semibold text-purple-600 uppercase tracking-widest border-b"
          >
            ИИ (OpenAI)
          </div>
          <table class="w-full text-sm">
            <tbody>
              <tr class="border-b bg-purple-50/40">
                <td class="px-4 py-2 w-8 text-center text-purple-400">
                  <i class="pi pi-external-link text-xs" />
                </td>
                <td class="px-4 py-2" colspan="2">
                  <a
                    href="https://platform.openai.com/usage"
                    target="_blank"
                    class="text-xs text-purple-600 underline font-medium"
                    >platform.openai.com/usage</a
                  >
                </td>
              </tr>
              <tr class="border-b">
                <td class="px-4 py-2.5 w-8 text-center cursor-default" title="OpenAI:ApiKey">
                  <span :class="appSettings.ai.apiKeyConfigured ? 'text-green-500' : 'text-red-500'"
                    ><i
                      :class="
                        appSettings.ai.apiKeyConfigured
                          ? 'pi pi-check-circle'
                          : 'pi pi-exclamation-triangle'
                      "
                  /></span>
                </td>
                <td class="px-4 py-2.5 text-gray-700">API Key</td>
                <td class="px-4 py-2.5 text-right text-xs text-gray-400 font-mono">
                  {{ appSettings.ai.apiKeyConfigured ? '••••' : '—' }}
                </td>
              </tr>
              <tr class="border-b">
                <td
                  class="px-4 py-2.5 w-8 text-center text-green-500 cursor-default"
                  title="OpenAI:Model"
                >
                  <i class="pi pi-box" />
                </td>
                <td class="px-4 py-2.5 text-gray-700">Модель</td>
                <td class="px-4 py-2.5 text-right text-xs text-gray-400 font-mono">
                  {{ appSettings.ai.model }}
                </td>
              </tr>
              <tr class="border-b">
                <td
                  class="px-4 py-2.5 w-8 text-center text-green-500 cursor-default"
                  title="OpenAI:InputPrice"
                >
                  <i class="pi pi-arrow-right" />
                </td>
                <td class="px-4 py-2.5 text-gray-700">Цена входа (за 1K токенов)</td>
                <td class="px-4 py-2.5 text-right text-xs text-gray-400 font-mono">
                  ${{ appSettings.ai.inputPricePer1K }}
                </td>
              </tr>
              <tr>
                <td
                  class="px-4 py-2.5 w-8 text-center text-green-500 cursor-default"
                  title="OpenAI:OutputPrice"
                >
                  <i class="pi pi-arrow-left" />
                </td>
                <td class="px-4 py-2.5 text-gray-700">Цена выхода (за 1K токенов)</td>
                <td class="px-4 py-2.5 text-right text-xs text-gray-400 font-mono">
                  ${{ appSettings.ai.outputPricePer1K }}
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <!-- Тесты -->
        <div class="border rounded-xl overflow-hidden shadow-sm border-l-4 border-l-amber-400">
          <div
            class="px-4 py-2.5 bg-gray-50 text-xs font-semibold text-amber-600 uppercase tracking-widest border-b"
          >
            Тесты
          </div>
          <table class="w-full text-sm">
            <tbody>
              <tr class="border-b bg-amber-50/40">
                <td class="px-4 py-2 w-8 text-center text-amber-400">
                  <i class="pi pi-external-link text-xs" />
                </td>
                <td class="px-4 py-2" colspan="2">
                  <a
                    href="https://www.google.com/recaptcha/admin"
                    target="_blank"
                    class="text-xs text-amber-600 underline font-medium"
                    >Google reCAPTCHA Admin</a
                  >
                </td>
              </tr>
              <tr class="border-b">
                <td class="px-4 py-2.5 w-8 text-center cursor-default" title="ReCaptcha:SecretKey">
                  <span
                    :class="
                      appSettings.tests.reCaptchaConfigured ? 'text-green-500' : 'text-red-500'
                    "
                    ><i
                      :class="
                        appSettings.tests.reCaptchaConfigured
                          ? 'pi pi-check-circle'
                          : 'pi pi-exclamation-triangle'
                      "
                  /></span>
                </td>
                <td class="px-4 py-2.5 text-gray-700">reCAPTCHA Secret Key</td>
                <td class="px-4 py-2.5 text-right text-xs text-gray-400 font-mono">
                  {{ appSettings.tests.reCaptchaConfigured ? '••••' : '—' }}
                </td>
              </tr>
              <tr>
                <td
                  class="px-4 py-2.5 w-8 text-center text-green-500 cursor-default"
                  title="TestsOptions:NumberOfShowingResultsForFree"
                >
                  <i class="pi pi-list" />
                </td>
                <td class="px-4 py-2.5 text-gray-700">Бесплатных результатов</td>
                <td class="px-4 py-2.5 text-right text-xs text-gray-400 font-mono">
                  {{ appSettings.tests.freeResultsCount }}
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <!-- Telegram -->
        <div class="border rounded-xl overflow-hidden shadow-sm border-l-4 border-l-sky-400">
          <div
            class="px-4 py-2.5 bg-gray-50 text-xs font-semibold text-sky-600 uppercase tracking-widest border-b"
          >
            Telegram
          </div>
          <table class="w-full text-sm">
            <tbody>
              <tr class="border-b">
                <td
                  class="px-4 py-2.5 w-8 text-center cursor-default"
                  title="TelegramOptions:ApiKey"
                >
                  <span
                    :class="
                      appSettings.telegram.apiKeyConfigured ? 'text-green-500' : 'text-red-500'
                    "
                    ><i
                      :class="
                        appSettings.telegram.apiKeyConfigured
                          ? 'pi pi-check-circle'
                          : 'pi pi-exclamation-triangle'
                      "
                  /></span>
                </td>
                <td class="px-4 py-2.5 text-gray-700">Bot API Key</td>
                <td class="px-4 py-2.5 text-right text-xs text-gray-400 font-mono">
                  {{ appSettings.telegram.apiKeyConfigured ? '••••' : '—' }}
                </td>
              </tr>
              <tr>
                <td
                  class="px-4 py-2.5 w-8 text-center cursor-default"
                  title="TelegramOptions:ChatId"
                >
                  <span :class="appSettings.telegram.chatId ? 'text-green-500' : 'text-red-500'"
                    ><i
                      :class="
                        appSettings.telegram.chatId
                          ? 'pi pi-check-circle'
                          : 'pi pi-exclamation-triangle'
                      "
                  /></span>
                </td>
                <td class="px-4 py-2.5 text-gray-700">Chat ID</td>
                <td class="px-4 py-2.5 text-right text-xs text-gray-400 font-mono">
                  {{ appSettings.telegram.chatId || '—' }}
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <!-- Аналитика -->
        <div class="border rounded-xl overflow-hidden shadow-sm border-l-4 border-l-pink-400">
          <div
            class="px-4 py-2.5 bg-gray-50 text-xs font-semibold text-pink-600 uppercase tracking-widest border-b"
          >
            Аналитика
          </div>
          <table class="w-full text-sm">
            <tbody>
              <tr class="border-b bg-pink-50/40">
                <td class="px-4 py-2 w-8 text-center text-pink-400">
                  <i class="pi pi-external-link text-xs" />
                </td>
                <td class="px-4 py-2" colspan="2">
                  <a
                    href="https://metrika.yandex.kz/"
                    target="_blank"
                    class="text-xs text-pink-600 underline font-medium"
                    >Яндекс.Метрика</a
                  >
                </td>
              </tr>
              <tr class="border-b bg-pink-50/40">
                <td class="px-4 py-2 w-8 text-center text-pink-400">
                  <i class="pi pi-external-link text-xs" />
                </td>
                <td class="px-4 py-2" colspan="2">
                  <a
                    href="https://clarity.microsoft.com/projects/view/qr85nu17d7/dashboard"
                    target="_blank"
                    class="text-xs text-pink-600 underline font-medium"
                    >Microsoft Clarity</a
                  >
                </td>
              </tr>
              <tr class="border-b">
                <td class="px-4 py-2.5 w-8 text-center cursor-default" title="Clarity:IsEnabled">
                  <span
                    :class="
                      appSettings.analytics.clarityIsEnabled ? 'text-green-500' : 'text-gray-400'
                    "
                    ><i
                      :class="
                        appSettings.analytics.clarityIsEnabled
                          ? 'pi pi-check-circle'
                          : 'pi pi-circle'
                      "
                  /></span>
                </td>
                <td class="px-4 py-2.5 text-gray-700">Microsoft Clarity</td>
                <td class="px-4 py-2.5 text-right text-xs text-gray-400 font-mono">
                  {{ appSettings.analytics.clarityIsEnabled ? 'Включена' : 'Отключена' }}
                </td>
              </tr>
              <tr>
                <td
                  class="px-4 py-2.5 w-8 text-center text-green-500 cursor-default"
                  title="Clarity:ClarityId"
                >
                  <i class="pi pi-tag" />
                </td>
                <td class="px-4 py-2.5 text-gray-700">Clarity ID</td>
                <td class="px-4 py-2.5 text-right text-xs text-gray-400 font-mono">
                  {{ appSettings.analytics.clarityId || '—' }}
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <!-- Инфраструктура -->
        <div class="border rounded-xl overflow-hidden shadow-sm border-l-4 border-l-gray-400">
          <div
            class="px-4 py-2.5 bg-gray-50 text-xs font-semibold text-gray-500 uppercase tracking-widest border-b"
          >
            Инфраструктура
          </div>
          <table class="w-full text-sm">
            <tbody>
              <tr class="border-b">
                <td
                  class="px-4 py-2.5 w-8 text-center cursor-default"
                  title="ConnectionStrings:Database"
                >
                  <span :class="appSettings.infra.dbConfigured ? 'text-green-500' : 'text-red-500'"
                    ><i
                      :class="
                        appSettings.infra.dbConfigured
                          ? 'pi pi-check-circle'
                          : 'pi pi-exclamation-triangle'
                      "
                  /></span>
                </td>
                <td class="px-4 py-2.5 text-gray-700">База данных</td>
                <td class="px-4 py-2.5 text-right text-xs text-gray-400 font-mono">
                  {{ appSettings.infra.dbConfigured ? '••••' : '—' }}
                </td>
              </tr>
              <tr>
                <td
                  class="px-4 py-2.5 w-8 text-center text-green-500 cursor-default"
                  title="AdminUrl + /api/hangfire"
                >
                  <i class="pi pi-server" />
                </td>
                <td class="px-4 py-2.5 text-gray-700">Hangfire Dashboard</td>
                <td class="px-4 py-2.5 text-right text-xs font-mono">
                  <a
                    v-if="appSettings.infra.hangfireUrl"
                    :href="appSettings.infra.hangfireUrl"
                    target="_blank"
                    class="text-blue-500 hover:underline"
                    >{{ appSettings.infra.hangfireUrl }}</a
                  >
                  <span v-else class="text-gray-400">—</span>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <!-- Фронтенд пользователя -->
        <div class="border rounded-xl overflow-hidden shadow-sm border-l-4 border-l-teal-400">
          <div
            class="px-4 py-2.5 bg-gray-50 text-xs font-semibold text-teal-600 uppercase tracking-widest border-b"
          >
            Фронтенд пользователя
          </div>
          <table class="w-full text-sm">
            <tbody>
              <tr class="border-b bg-teal-50/40">
                <td class="px-4 py-2 w-8 text-center text-teal-400">
                  <i class="pi pi-external-link text-xs" />
                </td>
                <td class="px-4 py-2" colspan="2">
                  <a
                    href="https://dashboard.tawk.to/"
                    target="_blank"
                    class="text-xs text-teal-600 underline font-medium"
                    >Tawk.to Dashboard</a
                  >
                </td>
              </tr>
              <tr class="border-b">
                <td class="px-4 py-2.5 w-8 text-center cursor-default" title="VITE_CLARITY_KEY">
                  <span :class="userFrontendKeys.clarityKey ? 'text-green-500' : 'text-red-500'"
                    ><i
                      :class="
                        userFrontendKeys.clarityKey
                          ? 'pi pi-check-circle'
                          : 'pi pi-exclamation-triangle'
                      "
                  /></span>
                </td>
                <td class="px-4 py-2.5 text-gray-700">Microsoft Clarity Key</td>
                <td class="px-4 py-2.5 text-right text-xs text-gray-400 font-mono">
                  {{ userFrontendKeys.clarityKey ? '••••' : '—' }}
                </td>
              </tr>
              <tr class="border-b">
                <td
                  class="px-4 py-2.5 w-8 text-center cursor-default"
                  title="VITE_TIPTOP_PUBLIC_KEY"
                >
                  <span
                    :class="userFrontendKeys.tipTopPublicKey ? 'text-green-500' : 'text-red-500'"
                    ><i
                      :class="
                        userFrontendKeys.tipTopPublicKey
                          ? 'pi pi-check-circle'
                          : 'pi pi-exclamation-triangle'
                      "
                  /></span>
                </td>
                <td class="px-4 py-2.5 text-gray-700">TipTop Public Key</td>
                <td class="px-4 py-2.5 text-right text-xs text-gray-400 font-mono">
                  {{ userFrontendKeys.tipTopPublicKey ? '••••' : '—' }}
                </td>
              </tr>
              <tr class="border-b">
                <td
                  class="px-4 py-2.5 w-8 text-center cursor-default"
                  title="VITE_YANDEX_ANALYTICS_ID"
                >
                  <span
                    :class="userFrontendKeys.yandexAnalyticsId ? 'text-green-500' : 'text-red-500'"
                    ><i
                      :class="
                        userFrontendKeys.yandexAnalyticsId
                          ? 'pi pi-check-circle'
                          : 'pi pi-exclamation-triangle'
                      "
                  /></span>
                </td>
                <td class="px-4 py-2.5 text-gray-700">Яндекс.Метрика ID</td>
                <td class="px-4 py-2.5 text-right text-xs text-gray-400 font-mono">
                  {{ userFrontendKeys.yandexAnalyticsId ? '••••' : '—' }}
                </td>
              </tr>
              <tr>
                <td
                  class="px-4 py-2.5 w-8 text-center text-green-500 cursor-default"
                  title="Захардкожен в src/plugins/tawkTo.ts"
                >
                  <i class="pi pi-check-circle" />
                </td>
                <td class="px-4 py-2.5 text-gray-700">Tawk.to виджет</td>
                <td class="px-4 py-2.5 text-right text-xs text-gray-400 font-mono">••••</td>
              </tr>
            </tbody>
          </table>
        </div>

        <!-- Языки -->
        <div class="border rounded-xl overflow-hidden shadow-sm border-l-4 border-l-indigo-400">
          <div
            class="px-4 py-2.5 bg-gray-50 text-xs font-semibold text-indigo-600 uppercase tracking-widest border-b"
          >
            Языки
          </div>
          <table class="w-full text-sm">
            <tbody>
              <tr
                v-for="lang in appSettings.languages"
                :key="lang.code"
                class="border-b last:border-b-0"
              >
                <td
                  class="px-4 py-2.5 w-8 text-center cursor-default"
                  title="БД: SupportedLanguages"
                >
                  <span :class="lang.enabled ? 'text-green-500' : 'text-gray-300'"
                    ><i :class="lang.enabled ? 'pi pi-check-circle' : 'pi pi-circle'"
                  /></span>
                </td>
                <td class="px-4 py-2.5 text-gray-700">{{ lang.name }}</td>
                <td class="px-4 py-2.5 text-right text-xs text-gray-400 font-mono">
                  {{ lang.code }}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>

    <!-- Шаблоны уведомлений -->
    <div v-if="templateRows.length" class="mt-4">
      <h2 class="text-base font-semibold text-gray-700 mb-3">Шаблоны уведомлений</h2>
      <div class="border rounded-xl overflow-hidden shadow-sm">
        <table class="w-full text-sm">
          <thead>
            <tr class="border-b bg-gray-50 text-gray-500 text-xs uppercase tracking-widest">
              <th class="px-4 py-2.5 text-left font-semibold">Шаблон</th>
              <th class="px-4 py-2.5 text-center font-semibold">SMS</th>
              <th class="px-4 py-2.5 text-center font-semibold">Email</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="row in templateRows" :key="row.code" class="border-b last:border-b-0">
              <td class="px-4 py-2.5 font-mono text-xs text-gray-700">{{ row.code }}</td>
              <td class="px-4 py-2.5 text-center">
                <span class="flex flex-wrap gap-1 justify-center">
                  <template v-if="enabledLanguages.length">
                    <span
                      v-for="lang in enabledLanguages"
                      :key="lang.code"
                      class="inline-block px-1.5 py-0.5 rounded text-[10px] font-mono"
                      :class="
                        row.smsLangs.includes(lang.code)
                          ? 'bg-green-100 text-green-700'
                          : 'bg-red-100 text-red-600'
                      "
                      >{{ lang.code }}</span
                    >
                  </template>
                  <template v-else>
                    <span
                      v-for="lc in row.smsLangs"
                      :key="lc"
                      class="inline-block px-1.5 py-0.5 rounded text-[10px] bg-green-100 text-green-700 font-mono"
                      >{{ lc }}</span
                    >
                    <span
                      v-if="!row.smsLangs.length"
                      class="inline-block px-1.5 py-0.5 rounded text-[10px] bg-red-100 text-red-600 font-mono"
                      >нет</span
                    >
                  </template>
                </span>
              </td>
              <td class="px-4 py-2.5 text-center">
                <span class="flex flex-wrap gap-1 justify-center">
                  <template v-if="enabledLanguages.length">
                    <span
                      v-for="lang in enabledLanguages"
                      :key="lang.code"
                      class="inline-block px-1.5 py-0.5 rounded text-[10px] font-mono"
                      :class="
                        row.emailLangs.includes(lang.code)
                          ? 'bg-green-100 text-green-700'
                          : 'bg-red-100 text-red-600'
                      "
                      >{{ lang.code }}</span
                    >
                  </template>
                  <template v-else>
                    <span
                      v-for="lc in row.emailLangs"
                      :key="lc"
                      class="inline-block px-1.5 py-0.5 rounded text-[10px] bg-green-100 text-green-700 font-mono"
                      >{{ lc }}</span
                    >
                    <span
                      v-if="!row.emailLangs.length"
                      class="inline-block px-1.5 py-0.5 rounded text-[10px] bg-red-100 text-red-600 font-mono"
                      >нет</span
                    >
                  </template>
                </span>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  </div>
</template>

<style scoped></style>
